import axios from 'axios';
import { useCSPCompliantStyles } from '@/composables/ui/useCSPCompliantStyles';

/**
 * Error reporting service for capturing and submitting front-end errors
 */
export function useErrorReportingService() {
  const apiClient = axios.create({
    baseURL: '/api',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });
  
  // Use CSP-compliant styles
  const { addStyleElement } = useCSPCompliantStyles();
  
  /**
   * Log a form submission error 
   * 
   * @param {string} formType - Type of form (home, auto, etc.)
   * @param {Error|Object} error - The error object that occurred
   * @param {Object} context - Additional context about what the user was doing
   * @returns {Promise} Promise that resolves when error is logged
   */
  const logFormError = async (formType, error, context = {}) => {
    try {
      // Prepare the error data
      const errorData = {
        form_type: formType,
        error_message: error.message || 'Unknown error',
        stack_trace: error.stack || '',
        browser_info: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
        },
        timestamp: new Date().toISOString(),
        context: context,
      };
      
      // If the error has a response property (like axios errors), include that
      if (error.response) {
        errorData.response = {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        };
      }
      
      // Submit to error endpoint
      const response = await apiClient.post('/error-report', errorData);
      console.log('Error reported successfully:', response.data);
      return response.data;
    } catch (reportingError) {
      // Avoid infinite loops if error reporting itself fails
      console.error('Failed to report error:', reportingError);
      return false;
    }
  };
  
  /**
   * Show a user-facing error message
   * 
   * @param {string} message - Error message to display
   * @param {Object} options - Additional options for the error
   * @returns {HTMLElement} The created error element
   */
  const showUserErrorMessage = (message, options = {}) => {
    const defaults = {
      contactEmail: 'policychanges@insuranceexpress.com',
      showContactInfo: true,
      duration: 0, // 0 means don't auto-dismiss
      severity: 'error' // error, warning, info
    };
    
    const settings = { ...defaults, ...options };
    
    // Create and show error notification
    showErrorNotification(message, settings);
    
    return null; // Return null as the error element is now managed by the showErrorNotification function
  };
  
  /**
   * Setup Vue global error handler
   * 
   * @param {Object} app - Vue app instance
   */
  const setupVueErrorHandler = (app) => {
    app.config.errorHandler = (err, instance, info) => {
      console.error('Vue Error:', err);
      
      // Log the error with additional component context
      logFormError('vue-component', err, {
        type: 'vueError',
        info: info,
        component: instance ? {
          name: instance.$options?.name || 'UnknownComponent',
          file: instance.$options?.__file,
          props: instance.$props ? JSON.stringify(instance.$props) : 'none'
        } : 'No component instance'
      });
      
      // Special handling for initialization errors
      if (err.message && err.message.includes('Cannot access') && err.message.includes('before initialization')) {
        showUserErrorMessage(
          'We encountered an error initializing this page. Please try again or contact support.',
          {
            contactEmail: 'policychanges@insuranceexpress.com',
            severity: 'error'
          }
        );
      }
    };
  };
  
  /**
   * Setup global error handlers to automatically capture unhandled errors
   */
  const setupGlobalErrorHandlers = () => {
    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      logFormError('global', event.reason, {
        type: 'unhandledRejection',
        message: event.reason?.message || 'Unhandled Promise Rejection'
      });
      
      showUserErrorMessage(
        'An unexpected error occurred. Please try again or contact support.',
        {
          contactEmail: 'policychanges@insuranceexpress.com',
          severity: 'error'
        }
      );
    });
    
    // Capture regular JS errors
    window.addEventListener('error', (event) => {
      logFormError('global', {
        message: event.message,
        stack: event.error?.stack,
      }, {
        type: 'uncaughtError',
        source: event.filename,
        line: event.lineno,
        column: event.colno
      });
      
      // Only show user message for actual errors, not for missing resources
      if (!event.filename.includes('.jpg') && 
          !event.filename.includes('.png') && 
          !event.filename.includes('.svg') &&
          !event.filename.includes('.css')) {
        showUserErrorMessage(
          'An error occurred. Please try again or contact support.',
          {
            contactEmail: 'policychanges@insuranceexpress.com',
            severity: 'error'
          }
        );
      }
    });
  };
  
  // New method to show error notification
  function showErrorNotification(message, settings = {}) {
    const errorContainer = getOrCreateErrorContainer();
    
    const errorElement = document.createElement('div');
    errorElement.className = 'error-notification';
    errorElement.setAttribute('data-severity', settings.severity || 'error');
    
    let content = `<div class="error-title">
        ${settings.severity === 'error' ? 'Error' : 
          settings.severity === 'warning' ? 'Warning' : 'Notice'}
    </div>
    <div class="error-message">${message}</div>`;
    
    if (settings.showContactInfo) {
        content += `<div class="error-contact">
            Need help? 
            <a href="mailto:${settings.contactEmail}" class="error-contact-link">
                Contact Support
            </a>
        </div>`;
    }
    
    errorElement.innerHTML = content;
    
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '&times;';
    closeButton.className = 'error-close-button';
    closeButton.onclick = () => {
        errorElement.remove();
        if (errorContainer.children.length === 0) {
            errorContainer.remove();
        }
    };
    
    errorElement.appendChild(closeButton);
    errorContainer.appendChild(errorElement);
    
    // Auto-remove after delay
    if (settings.autoRemove !== false) {
        setTimeout(() => {
            if (errorElement.parentNode) {
                errorElement.remove();
                if (errorContainer.children.length === 0) {
                    errorContainer.remove();
                }
            }
        }, settings.duration || 10000);
    }
  }

  function getOrCreateErrorContainer() {
    let errorContainer = document.getElementById('error-notification-container');
    if (!errorContainer) {
        errorContainer = document.createElement('div');
        errorContainer.id = 'error-notification-container';
        errorContainer.className = 'error-container';
        document.body.appendChild(errorContainer);
        
        // Add CSS styles using nonce if available
        addErrorStyles();
    }
    return errorContainer;
  }

  function addErrorStyles() {
    const css = `
      .error-container {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 9999;
      }
      
      .error-notification {
        background-color: #f44336;
        color: white;
        padding: 16px;
        margin-top: 10px;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.2);
        width: 350px;
        max-width: 90vw;
        position: relative;
      }
      
      .error-notification[data-severity="warning"] {
        background-color: #ff9800;
      }
      
      .error-notification[data-severity="info"] {
        background-color: #2196f3;
      }
      
      .error-title {
        font-weight: bold;
        margin-bottom: 8px;
      }
      
      .error-message {
        margin-bottom: 8px;
      }
      
      .error-contact {
        font-size: 0.9em;
      }
      
      .error-contact-link {
        color: white;
        text-decoration: underline;
      }
      
      .error-close-button {
        position: absolute;
        top: 8px;
        right: 8px;
        background-color: transparent;
        border: none;
        color: white;
        font-size: 20px;
        cursor: pointer;
      }
    `;
    
    addStyleElement('error-notification-styles', css);
  }
  
  return {
    logFormError,
    setupGlobalErrorHandlers,
    showUserErrorMessage,
    setupVueErrorHandler
  };
} 